import React from 'react';
import { Button } from './ui/button';
import { UserPlus, MapPin, Calculator, TrendingUp, CheckCircle2 } from 'lucide-react';

const Process = ({ onSetupClick }) => {
  const steps = [
    {
      icon: UserPlus,
      title: 'Quick Sign Up',
      description: 'Get started in under 2 minutes with Google authentication',
      time: '30 seconds',
      features: ['Google Sign-in', 'Phone verification', 'Secure profile']
    },
    {
      icon: MapPin,
      title: 'GPS Farm Registration',
      description: 'Automatically capture your farm location and size',
      time: '1 minute',
      features: ['GPS auto-detect', 'Farm mapping', 'Land verification']
    },
    {
      icon: Calculator,
      title: 'Biochar Estimation',
      description: 'Calculate your potential earnings and environmental impact',
      time: '30 seconds',
      features: ['Waste analysis', 'Yield calculation', 'Earning projection']
    },
    {
      icon: TrendingUp,
      title: 'Start Earning',
      description: 'Begin your sustainable farming journey with immediate benefits',
      time: 'Immediate',
      features: ['Carbon credits', 'Market access', 'Technical support']
    }
  ];

  return (
    <section id="process" className="pt-0 pb-0 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16 pt-20">
          <p className="text-neo-emerald font-semibold mb-2 font-body">Join 10,000+ Smart Farmers</p>
          <h2 className="text-4xl font-heading font-bold text-neo-black mb-4">
            Transform Your Farm Into a Climate Solution
          </h2>
          <p className="text-lg text-neo-gray max-w-2xl mx-auto font-body">
            Complete setup in under 3 minutes and start earning from agricultural waste while improving soil health
          </p>
          <p className="text-sm text-neo-gray mt-4 font-body">Setup Time: 2-3 minutes</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-0">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-neo-emerald/30 -z-10"></div>
                )}
                <div className="bg-gray-50 border-2 border-neo-emerald/20 rounded-2xl p-6 space-y-4 hover:border-neo-emerald/40 hover:shadow-lg hover:shadow-neo-emerald/20 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="w-14 h-14 bg-neo-emerald/20 rounded-xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-neo-emerald" />
                    </div>
                    <span className="text-sm font-semibold text-neo-emerald bg-neo-emerald/10 px-3 py-1 rounded-full font-body">
                      {step.time}
                    </span>
                  </div>
                  <h3 className="text-xl font-heading font-bold text-neo-black">{step.title}</h3>
                  <p className="text-neo-gray text-sm font-body">{step.description}</p>
                  <div className="space-y-2 pt-2">
                    {step.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-neo-gray font-body">
                        <CheckCircle2 className="w-4 h-4 text-neo-emerald flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-neo-emerald/10 to-neo-emerald/5 border border-neo-emerald/20 rounded-2xl p-8 mt-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-heading font-bold text-neo-black mb-4">Why Choose BLOOMCYCLE INNOVATIONS?</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Improve soil health by 40%',
                  'Reduce waste disposal costs',
                  'Earn from carbon credits',
                  'Access premium markets',
                  'Government scheme integration',
                  '24/7 technical support'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-neo-emerald flex-shrink-0" />
                    <span className="text-neo-gray text-sm font-body">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4 shadow-sm">
                <p className="text-sm text-neo-gray mb-1 font-body">Average Soil Improvement</p>
                <p className="text-3xl font-heading font-bold text-neo-emerald">40%</p>
              </div>
              <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4 shadow-sm">
                <p className="text-sm text-neo-gray mb-1 font-body">Carbon Credits Earned</p>
                <p className="text-3xl font-heading font-bold text-neo-emerald">2.5 tons CO₂/year</p>
              </div>
              <div className="bg-gray-50 border border-neo-emerald/20 rounded-xl p-4 shadow-sm">
                <p className="text-sm text-neo-gray mb-1 font-body">Additional Revenue</p>
                <p className="text-3xl font-heading font-bold text-neo-emerald">₹15,000+/year</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
